import { z } from "zod";
import { planTierSchema } from "./users.js";
// ---------------------------------------------------------------------------
// TASK-036 — Telegram Stars (XTR) invoice surface (shared types).
//
// MVP Pro is the only paid plan. The wire shape is intentionally narrow so
// future plans (e.g. AGENCY in Phase 2) extend it via the same `plan`
// discriminator without breaking the v1 client.
//
// The bot-defined invoice `payload` is a JSON string (Telegram constraint:
// 1-128 bytes). Both the Mini App rail (createInvoiceLink) and the bot-chat
// rail (sendInvoice) emit the SAME payload shape so the successful_payment
// webhook handler (TASK-037) can dispatch off a single Zod parse without
// branching on which surface produced it.
// ---------------------------------------------------------------------------
/**
 * Plans accepted by the Stars invoice endpoint. MVP allows PRO only; the
 * schema is a `z.enum` (not the broader planTierSchema) so a request body
 * with `plan: "FREE"` or `plan: "AGENCY"` is rejected with a 400 at the
 * boundary instead of leaking to the invoice creation step.
 */
export const starsInvoicePlanSchema = z.enum(["PRO"]);
/**
 * POST /api/payments/stars/invoice request body.
 */
export const starsInvoiceRequestSchema = z
    .object({
    plan: starsInvoicePlanSchema,
})
    .strict();
/**
 * POST /api/payments/stars/invoice response.
 *
 * `invoiceLink` is the URL returned by Telegram's `createInvoiceLink`. The
 * Mini App opens it via `Telegram.WebApp.openInvoice(invoiceLink)`.
 *
 * `payloadNonce` is the same nonce embedded in the bot-defined invoice
 * payload. Returned to the client so the Mini App can correlate the
 * subsequent `successful_payment` webhook (it's the only stable id the
 * client knows BEFORE Telegram mints `telegram_payment_charge_id`).
 *
 * `xtrAmount` is the Stars amount displayed to the user (informational —
 * Telegram's UI shows the same number from the invoice, but having it on
 * the response makes the Mini App's confirmation copy self-contained).
 */
export const starsInvoiceResponseSchema = z
    .object({
    invoiceLink: z.string().url(),
    plan: starsInvoicePlanSchema,
    xtrAmount: z.number().int().positive(),
    payloadNonce: z.string().min(1),
})
    .strict();
/**
 * Bot-defined invoice payload (the JSON-encoded string handed to Telegram's
 * `createInvoiceLink` / `sendInvoice` `payload` parameter).
 *
 * Why every field:
 *   - `v`: schema version. Bumped on any breaking change. The webhook
 *     handler refuses unknown versions so a future format never accidentally
 *     replays through old logic.
 *   - `userId`: the internal cuid of the User the invoice was minted for.
 *     The webhook compares `from.id` (Telegram id) against the User row
 *     resolved from `userId` in pre_checkout_query — a mismatch means a
 *     payload was minted for one user and clicked by another (shouldn't
 *     happen via Telegram's UI but defended against anyway).
 *   - `plan`: which plan the payment unlocks. PRO only in MVP.
 *   - `nonce`: a UUID per invoice. Used both for client/server correlation
 *     and as the Redis dedup key in pre_checkout_query (TASK-037).
 *
 * The encoded JSON must stay ≤128 bytes to satisfy Telegram's payload
 * limit. With the current shape (cuid userId ~25 chars, "PRO", UUID 36
 * chars) the encoded form is comfortably under (~95 bytes) — but
 * `assertPayloadFitsTelegram` enforces this at runtime so a future
 * field addition fails loudly instead of silently truncating.
 */
export const starsInvoicePayloadSchema = z
    .object({
    v: z.literal(1),
    userId: z.string().min(1),
    plan: starsInvoicePlanSchema,
    nonce: z.string().min(1),
})
    .strict();
/** Telegram's hard cap on the bot-defined invoice payload, in bytes. */
export const TELEGRAM_INVOICE_PAYLOAD_MAX_BYTES = 128;
/** Telegram's hard cap on the invoice title (1-32 chars). */
export const TELEGRAM_INVOICE_TITLE_MAX_CHARS = 32;
/** Telegram's hard cap on the invoice description (1-255 chars). */
export const TELEGRAM_INVOICE_DESCRIPTION_MAX_CHARS = 255;
/**
 * Encode an invoice payload to its on-the-wire JSON string and assert it
 * fits Telegram's 128-byte limit. Throws on overflow rather than silently
 * truncating — a truncated payload would deserialize to garbage on the
 * webhook side.
 */
export function encodeInvoicePayload(payload) {
    const encoded = JSON.stringify(payload);
    // UTF-8 byte length, not character count — Telegram counts bytes.
    const byteLength = new TextEncoder().encode(encoded).length;
    if (byteLength > TELEGRAM_INVOICE_PAYLOAD_MAX_BYTES) {
        throw new Error(`Invoice payload exceeds Telegram's ${TELEGRAM_INVOICE_PAYLOAD_MAX_BYTES}-byte limit (${byteLength} bytes). Reduce field sizes or bump version.`);
    }
    return encoded;
}
/**
 * Decode + validate an invoice payload from the webhook side. Returns the
 * parsed payload on success, or `null` on any decode/validation failure.
 * The webhook handler treats null as "reject the pre_checkout_query" —
 * malformed payloads are never charged.
 */
export function decodeInvoicePayload(raw) {
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return null;
    }
    const result = starsInvoicePayloadSchema.safeParse(parsed);
    return result.success ? result.data : null;
}
/**
 * Produce the invoice copy for a given plan. The text is intentionally
 * static (no per-user variables) so the same descriptor is reusable across
 * the Mini App rail and the bot-chat rail — the user sees identical
 * wording regardless of which surface initiated the upgrade.
 */
export function getPlanInvoiceDescriptor(plan) {
    if (plan === "PRO") {
        return {
            title: "Community Analytics Pro",
            description: "Unlock the full dashboard: lifecycle trends, alerts, weekly PDF reports, and up to 5 communities. One month — auto-billed monthly.",
            priceLabel: "Pro plan, 1 month",
        };
    }
    // The schema currently allows only PRO at compile time, but the switch is
    // structured so future plans surface as a TypeScript error here rather
    // than a runtime fallback that ships with empty copy.
    // istanbul ignore next
    throw new Error(`No invoice descriptor for plan: ${plan}`);
}
/**
 * Asserts that a candidate plan tier is one we know how to invoice. Used
 * by the `/upgrade` bot command — the user might already be on PRO or on
 * AGENCY (Phase 2), in which case we don't show an invoice.
 */
export function isInvoiceablePlan(plan) {
    return plan === "PRO";
}
// ---------------------------------------------------------------------------
// TASK-038 — GET /api/payments/subscription summary surface.
//
// Returned by both the GET endpoint (current snapshot of the user's
// subscription) and projected from the DELETE endpoint (the post-cancel
// snapshot — same shape so the Mini App can re-render off a single typed
// hook).
//
// SECURITY: this surface MUST NOT include provider secrets — no
// providerSubscriptionId, no telegram_payment_charge_id, no email/billing
// snapshots. Just the four fields the Mini App needs to render the plan
// state and the cancel CTA.
// ---------------------------------------------------------------------------
export const subscriptionStatusSchema = z.enum([
    "ACTIVE",
    "PAST_DUE",
    "CANCELED",
    "EXPIRED",
]);
/**
 * Wire shape for the user's current subscription. Returned by GET
 * /api/payments/subscription. When the user has never subscribed (no row
 * in DB), the route returns the FREE-tier shape: plan='FREE', status=null,
 * currentPeriodEnd=null, cancelAtPeriodEnd=false. We use null on the
 * date/status fields rather than omitting them so a single response shape
 * covers both cases — the Mini App can branch on `plan === 'FREE'` without
 * a discriminated union.
 */
export const subscriptionSummarySchema = z
    .object({
    plan: planTierSchema,
    status: subscriptionStatusSchema.nullable(),
    /** ISO 8601 timestamp; null only when the user has never subscribed. */
    currentPeriodEnd: z.string().datetime().nullable(),
    cancelAtPeriodEnd: z.boolean(),
})
    .strict();
//# sourceMappingURL=payments.js.map